import React, { useEffect, useMemo, useState } from 'react';
import Heading from '../../../../../../generic-components/Heading';
import { Input } from '../../../../../../generic-components/Input';
import Button from '../../../../../../generic-components/Button';
import { DISCOUNT_TYPES } from './Discount';
import { INPUT_REPLACE_TYPES } from '../../../../../../generic-components/Input/constants';
import Icon from '../../../../../../generic-components/Icon';
import CloseIcon from '../../../../../../assets/svg/x-icon-v2.svg';
import { useStore } from '../../../../../../store';

interface IProps {
  onClose: () => void;
  totalAmount: number;
}

export const EditDiscountForm = ({ onClose, totalAmount }: IProps) => {
  const [discountType, setDiscountType] = useState<string>(DISCOUNT_TYPES.FIXED);
  const [discountValue, setDiscountValue] = useState<string>('');
  const [shouldCleanValue, setShouldCleanValue] = useState<boolean>(false);
  const [errorState, setErrorState] = useState<{ isError: boolean; errMsg: string }>({ isError: false, errMsg: '' });
  const invoiceData = useStore((state) => state.invoiceData);
  const setInvoiceDataToUpdate = useStore((state) => state.setInvoiceDataToUpdate);

  const inputPlaceHolder = useMemo(() => {
    if (discountType === DISCOUNT_TYPES.PERCENT) {
      return 'Discount Value (%)';
    }
    return 'Discount Value ($)';
  }, [discountType]);

  const isSubmitAble = useMemo(() => {
    const cleanValue = discountValue.replace(/[^0-9.]+/g, '');
    if (!cleanValue || cleanValue === '0' || cleanValue === '%') return false;
    if (discountType === DISCOUNT_TYPES.PERCENT && Number(cleanValue) > 99.99) return false;
    if (discountType === DISCOUNT_TYPES.FIXED && Number(cleanValue) > totalAmount) return false;
    return true;
  }, [discountValue]);

  const inputReplaceType = useMemo(() => {
    if (discountType === DISCOUNT_TYPES.PERCENT) {
      return INPUT_REPLACE_TYPES.PERCENT;
    }
    return INPUT_REPLACE_TYPES.CURRENCY;
  }, [discountType]);

  const inputMaxPrice = useMemo(() => {
    if (discountType === DISCOUNT_TYPES.PERCENT) {
      return 99.99;
    }
    return 1000000;
  }, [discountType]);

  const handleSubmit = () => {
    const cleanValue = discountValue.replace(/[^0-9.]+/g, '');
    if (Number(cleanValue) && invoiceData) {
      const invoiceDataToUpdate = { ...invoiceData };
      if (invoiceDataToUpdate?.invoiceService) {
        invoiceDataToUpdate.invoiceService.discountType = discountType;
        invoiceDataToUpdate.invoiceService.discount =
          discountType === DISCOUNT_TYPES.PERCENT ? Number(cleanValue) / 100 : Number(cleanValue);
        setInvoiceDataToUpdate(invoiceDataToUpdate);
      }
    }
    onClose();
  };

  const handleChange = (inputData: any) => {
    setDiscountValue(inputData.value);
  };

  useEffect(() => {
    setDiscountValue('');
    setShouldCleanValue(true);
    setTimeout(() => {
      setShouldCleanValue(false);
    }, 100);
  }, [discountType]);

  useEffect(() => {
    const cleanValue = discountValue.replace(/[^0-9.]+/g, '');
    const intValue = Number(cleanValue);
    if (discountType === DISCOUNT_TYPES.PERCENT && intValue > 99.99) {
      setErrorState({ isError: true, errMsg: 'Discount cannot exceed 99.99%' });
    } else if (discountType === DISCOUNT_TYPES.FIXED && intValue >= totalAmount && intValue > 0) {
      setErrorState({ isError: true, errMsg: `Discount cannot exceed the subtotal amount $${totalAmount}` });
    } else {
      setErrorState({ isError: false, errMsg: '' });
    }
  }, [discountType, discountValue]);

  return (
    <div className='flex flex-col py-[24px] px-[24px]'>
      <div className='flex items-center justify-between grow'>
        <Heading title='Add Discount' className='font-metricRSemibold text-[24px] text-bw08 mb-[4px]' />
        <Icon onClick={onClose} src={CloseIcon} />
      </div>
      <Heading
        title='This discount will apply to the subtotal before tax.'
        className='font-metric text-bw07 text-[16px] mb-[20px]'
      />
      <div className='flex items-start w-full gap-[20px]'>
        <div className='basis-[70%]'>
          <Input
            shouldCleanValue={shouldCleanValue}
            replace={inputReplaceType}
            onChange={handleChange}
            maxPrice={inputMaxPrice}
            title={inputPlaceHolder}
            type='amount'
            isError={errorState.isError}
            error={errorState.errMsg}
            validation=''
            shouldValidate={false}
            id=''
          />
        </div>
        <div className='basis-[28%] flex items-center border-[2px] rounded-[8px] h-[64px] hover:bg-action00 border-action02 py-[0px] px-[0px] mb-[30px]'>
          <Button
            onClick={() => setDiscountType(DISCOUNT_TYPES.FIXED)}
            title='$'
            className={`basis-1/2 rounded-[8px]  h-[60px] text-bw08 h-full ${discountType === DISCOUNT_TYPES.FIXED ? 'bg-action00 text-action03  outline outline-[2px] outline-action03' : ''}`}
            id=''
          />
          <Button
            onClick={() => setDiscountType(DISCOUNT_TYPES.PERCENT)}
            title='%'
            className={`basis-1/2 rounded-[8px] h-[60px]  text-bw08 h-full ${discountType === DISCOUNT_TYPES.PERCENT ? 'bg-action00  outline outline-[2px] outline-action03' : ''}`}
            id=''
          />
        </div>
      </div>
      <div className='flex items-center justify-end grow gap-[12px]'>
        <Button
          onClick={onClose}
          className='rounded-[4px] py-[10px] px-[30px] font-metricRSemibold text-[14px] text-action03 bg-action01'
          id=''
          title='Cancel'
        />
        <Button
          disable={!isSubmitAble}
          onClick={handleSubmit}
          className={`rounded-[4px] py-[10px] px-[30px] font-metricRSemibold text-[14px] ${isSubmitAble ? 'text-bw00 bg-action03 hover:bg-action04' : 'text-bw00 bg-bw05'}`}
          id=''
          title='Apply'
        />
      </div>
    </div>
  );
};
